import React, { useState } from 'react'
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Select,
  MenuItem,
  Box,
  Typography,
  ButtonBase,
  TableSortLabel,
  Menu,
  Pagination,
} from '@mui/material'
import { EmployeeRow } from './DashboardGrid'
import { fonts } from '../../../config/Dashboardtheme'
import MpSvgIcon from '../atom/MpSvgIcon'
import ConfirmationModal from '../atom/ConfirmationModal'
import IndividualUserStats from '../atom/IndividualUserStats'
import { formatToReadableDate } from '@meprism/shared/src/utils/dateHelpers'

interface Props {
  rows: EmployeeRow[]
  page: number
  setPage: (page: number) => void
  sortedRows: EmployeeRow[]
  handleSort: (key: string) => void
  sortConfig: { key: string; direction: 'asc' | 'desc' }
  selectedStatus: string
  selectedBillingStatus: string
  handleBillingItemClick: (value: string) => void
  handleMenuItemClick: (value: string) => void
  openMenuIndex: number | null
  anchorEl: HTMLButtonElement | null
  isOpen: boolean
  detailsModalOpen: boolean
  handleDetailsModalOpen: () => void
  setDetailsModalOpen: (val: boolean) => void
  handleButtonClick: (event: React.MouseEvent<HTMLButtonElement>, index: number) => void
  close: () => void
  handleopen: () => void
  open: boolean
  setOpen: (val: boolean) => void
  muidrow: string | null
  dispatch: any
  resendInvite: (muid: string) => void
  buttonRef: React.RefObject<HTMLButtonElement>
}

const EmployeeTable: React.FC<Props> = ({
  rows,
  page,
  setPage,
  sortedRows,
  handleSort,
  sortConfig,
  selectedStatus,
  selectedBillingStatus,
  handleBillingItemClick,
  handleMenuItemClick,
  openMenuIndex,
  anchorEl,
  isOpen,
  handleDetailsModalOpen,
  detailsModalOpen,
  setDetailsModalOpen,
  handleButtonClick,
  close,
  handleopen,
  open,
  setOpen,
  muidrow,
  dispatch,
  resendInvite,
  buttonRef,
}) => {
  const rowsPerPage = 10
  const [userMuid, setUserMuid] = useState<string | null>(null)

  const columns = [
    { key: 'col1', name: 'Email ID', width: 250 },
    { key: 'col2', name: 'Found', width: 70 },
    { key: 'col3', name: 'Removed', width: 70 },
    { key: 'col4', name: 'In Progress', width: 140 },
    { key: 'col5', name: 'Status', width: 140 },
    { key: 'col6', name: 'Billing Status', width: 140 },
    { key: 'col7', name: '', width: 40 },
  ]

  const getBillingStatusColor = (status: string | null) => {
    switch (status) {
      case 'paid':
        return '#52c173'
      case 'expired':
      case 'unpaid':
      case null:
        return '#ef6762'
      default:
        return '#6C757D'
    }
  }

  const getInviteStatusColor = (status: string | null) => {
    switch (status) {
      case 'active':
        return '#52c173'
      case 'accepted':
        return '#27AE60'
      case 'invited':
      case 'queued':
      case 'expired':
      case 'failed':
      case 'deleted':
        return '#ef6762'
      default:
        return '#6C757D'
    }
  }
  const formatInviteStatus = (status: string | null) => {
    switch (status) {
      case 'queued':
      case 'invited':
        return 'Invited'
      case 'accepted':
        return 'Accepted'
      case 'expired':
        return 'Expired'
      case 'failed':
        return 'Failed'
      case 'deleted':
        return 'Deleted'
      case 'active':
        return 'Active'
      default:
        return status || 'No Invite'
    }
  }

  const formatBillingStatus = (status: string | null) => {
    switch (status) {
      case 'paid':
        return 'Paid'
      case 'unpaid':
        return 'Unpaid'
      case 'expired':
        return 'Expired'
      case 'waiting':
      case null:
        return 'Waiting'
      default:
        return status || 'N/A'
    }
  }

  const uniqueInviteStatuses = Array.from(
    new Set(rows.map((row) => row.col5))
  )

  const uniqueBillingStatuses = Array.from(
    new Set(rows.map((row) => row.col6 ?? 'waiting'))
  )

  return (
    <Box bgcolor="#fff" borderRadius={5} p={3}>
      <TableContainer>
        <Table style={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  onClick={() => ['col2', 'col3', 'col4'].includes(column.key) && handleSort(column.key)}
                  style={{
                    cursor: ['col2', 'col3', 'col4'].includes(column.key) ? 'pointer' : 'default',
                    borderBottom: '1px solid #426BF0',
                    ...fonts.Inter.SemiBold,
                    width: column.width,
                  }}
                >
                  {['col2', 'col3', 'col4'].includes(column.key) ? (
                    <TableSortLabel
                      active={sortConfig.key === column.key}
                      direction={sortConfig.key === column.key ? sortConfig.direction : 'asc'}
                    >
                      {column.name}
                    </TableSortLabel>
                  ) : column.key === 'col5' ? (
                    <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Select
                        value={selectedStatus}
                        onChange={(e) => handleMenuItemClick(e.target.value)}
                        displayEmpty
                        style={{ marginLeft: 8 }}
                        renderValue={(selected) => {
                        if (selected === '') {
                          return 'Invite Status';
                        }
                        return formatInviteStatus(selected);
                      }}
                      >
                      <MenuItem value="">All</MenuItem>
                        {uniqueInviteStatuses.map((status) => (
                          <MenuItem key={status} value={status}>
                            {formatInviteStatus(status)}
                          </MenuItem>
                      ))}
                      </Select>
                      </Box>
                    </>
                  ) : column.key === 'col6' ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Select
                        value={selectedBillingStatus}
                        onChange={(e) => handleBillingItemClick(e.target.value)}
                        displayEmpty
                        style={{ marginLeft: 8 }}
                        renderValue={(selected) => {
                          if (selected === '') {
                            return 'Billing Status';
                          }
                          return formatBillingStatus(selected);
                        }}
                      >
                        <MenuItem value="">All</MenuItem>
                        {uniqueBillingStatuses.map((status) => (
                          <MenuItem key={status} value={status}>
                            {formatBillingStatus(status)}
                          </MenuItem>
                      ))}
                      </Select>
                      </Box>
                    </>
                  ) : (
                    column.name
                  )}
                </TableCell>

              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((row) => (
              <TableRow key={row.id} style={{height: 77}}>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    style={{ 
                      borderBottom: '1px solid #615F6B', 
                      position: 'relative',
                      textAlign: ['col5', 'col6'].includes(column.key) ? 'center': 'left',
                      paddingLeft: column.key === 'col2' ? '33px' : 
                                   column.key === 'col3' ? '45px' : 
                                   column.key === 'col4' ? '50px' : undefined,

                    }}
                  >
                    {column.key === 'col5' ? (
                      <span style={{ color: getInviteStatusColor(row.col5)}}>
                              {formatInviteStatus(row.col5)}
                            </span>
                    ) : column.key === 'col6' ? (
                      <Typography sx={{ color: getBillingStatusColor(row.col6), fontWeight: 500 }}>
                        {formatBillingStatus(row.col6)}
                      </Typography>
                    ) : column.key === 'col7' ? (
                      <Box sx={{ backgroundColor: openMenuIndex === row.id ? '#ECF0FD' : 'white', borderRadius: 2 }}>
                        {row.col5 !== 'deleted' && (
                          <ButtonBase
                            onClick={(e) => handleButtonClick(e, row.id)}
                            ref={buttonRef}
                            aria-controls={isOpen ? 'simple-menu' : undefined}
                            aria-haspopup="menu"
                          >
                            <MpSvgIcon icon={openMenuIndex === row.id ? 'threeDotsBlue' : 'threeDotsNew'} />
                          </ButtonBase>
                        )}
                        <Menu open={openMenuIndex === row.id} onClose={close} anchorEl={anchorEl}>
                          <MenuItem
                            onClick={() => {
                              handleDetailsModalOpen()
                              setDetailsModalOpen(true)
                              if (row.col8) {
                                setUserMuid(row.col8)
                              }
                              close()
                            }}
                          >
                            <Typography variant="label">View Details</Typography>
                          </MenuItem>
                          <MenuItem onClick={() => { handleopen(); close(); }}>
                            <Typography variant="label">Remove Employee</Typography>
                          </MenuItem>
                          {row.col5 === 'invited' && (
                            <MenuItem disabled={!row.col8} onClick={() => row.col8 && dispatch(resendInvite(row.col8))}>
                              <Typography variant="label">Resend Invitation</Typography>
                            </MenuItem>
                          )}
                        </Menu>
                        <ConfirmationModal open={open} setOpen={setOpen} muid={muidrow} />
                      </Box>
                    ) : column.key === 'col1' ? (
                      <Box>
                        <Typography sx={{ fontSize: 15 }}>{row.col1}</Typography>
                        <Typography variant="caption" sx={{ color: '#C67EFF', fontSize: 12 }}>
                          {(row.col9 && formatToReadableDate(row.col9)) ? `Last Scan: ${formatToReadableDate(row.col9)}` : 'No scan results'}
                        </Typography>
                      </Box>
                    ) : (
                      row[column.key as keyof EmployeeRow]
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Pagination
          count={Math.ceil(sortedRows.length / rowsPerPage)}
          page={page + 1}
          onChange={(_, newPage) => setPage(newPage - 1)}
        />
      </Box>
      <IndividualUserStats open={detailsModalOpen} setOpen={setDetailsModalOpen} muid={userMuid} setMuid={setUserMuid}/>
    </Box>
  )
}

export default EmployeeTable
